import { z } from 'zod';
import type { Flag } from '../commands/envelope.js';
import { D } from '../internal/decimal.js';
import { type Cents, cents, mulQty } from '../money/index.js';
import type { PermissionKey } from '../permissions/index.js';
import { type QtyInput, absQty, isPositive, isZero, qty } from '../quantity/index.js';
import { isCutoff } from '../time/index.js';

/**
 * Approvals are **one system** (vault Core Model, governance): a write-off, a count
 * adjustment, a purchase request or a correction is the same request → decision shape,
 * recorded in `approval_request`. Two modes:
 *
 * - `manager_pin`: synchronous, at a station. A manager enters their PIN on the spot (the
 *   station checks it locally, offline too); the command carries the approver and the
 *   server re-checks the approver's permission, then records the approval and an audit
 *   entry.
 * - `async`: a request in the Admin inbox. A physical fact above the threshold that arrives
 *   **without** a valid manager-PIN approval is still applied (physical facts are never
 *   rejected, plan §9.3) — flagged `needs_approval`, with an async request a manager
 *   confirms, or rejects (which writes a compensating movement).
 *
 * Thresholds (DECISIONS D-100): write-offs from **R250**; count adjustments from **10% or
 * R250**. Each is an account setting.
 * @module
 */

/** What an approval is about (matches `approval_request.type`). */
export const APPROVAL_TYPES = [
  'write_off',
  'purchase_request',
  'adjustment',
  'correction',
] as const;
/** One of {@link APPROVAL_TYPES}. */
export type ApprovalType = (typeof APPROVAL_TYPES)[number];

/** How an approval is given (matches `approval_request.mode`). */
export const APPROVAL_MODES = ['async', 'manager_pin'] as const;
/** One of {@link APPROVAL_MODES}. */
export type ApprovalMode = (typeof APPROVAL_MODES)[number];

/** An approval's lifecycle (matches `approval_request.status`). */
export const APPROVAL_STATUSES = ['requested', 'approved', 'rejected', 'cancelled'] as const;
/** One of {@link APPROVAL_STATUSES}. */
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

/** A decision a person takes on a request. */
export const APPROVAL_DECISIONS = ['approve', 'reject', 'cancel'] as const;
/** One of {@link APPROVAL_DECISIONS}. */
export type ApprovalDecisionKind = (typeof APPROVAL_DECISIONS)[number];

/** The status a decision leads to. */
export function statusAfterDecision(decision: ApprovalDecisionKind): ApprovalStatus {
  switch (decision) {
    case 'approve':
      return 'approved';
    case 'reject':
      return 'rejected';
    case 'cancel':
      return 'cancelled';
  }
}

/**
 * Whether an approval may go from `from` to `to`: only `requested` moves, and only to a
 * final status. A decision is final (a mistake is corrected by a new request, never by
 * re-deciding).
 */
export function canTransitionApproval(from: ApprovalStatus, to: ApprovalStatus): boolean {
  return from === 'requested' && to !== 'requested';
}

/** The stock facts that need approval above a threshold. */
export const THRESHOLD_KINDS = ['write_off', 'count_adjustment'] as const;
/** One of {@link THRESHOLD_KINDS}. */
export type ThresholdKind = (typeof THRESHOLD_KINDS)[number];

/** The approval type recorded for a threshold kind. */
export function approvalTypeFor(kind: ThresholdKind): ApprovalType {
  return kind === 'write_off' ? 'write_off' : 'adjustment';
}

/**
 * The permission an approver needs: `writeoff.approve` for write-offs, `stock.adjust` for
 * count adjustments (plan §10.3).
 */
export function approverPermission(type: ApprovalType): PermissionKey | null {
  switch (type) {
    case 'write_off':
      return 'writeoff.approve';
    case 'adjustment':
    case 'correction':
      return 'stock.adjust';
    case 'purchase_request':
      return 'purchasing.approve';
  }
}

/** Approval thresholds (account settings; defaults D-100). */
export interface ApprovalThresholds {
  /** A write-off worth at least this needs approval. */
  writeOffMinCents: Cents;
  /** A count adjustment of at least this percentage of the level before needs approval. */
  countAdjustPct: string;
  /** A count adjustment worth at least this needs approval. */
  countAdjustMinCents: Cents;
}

/** D-100 defaults: R250 write-offs; count adjustments 10% or R250. */
export const DEFAULT_APPROVAL_THRESHOLDS: Readonly<ApprovalThresholds> = Object.freeze({
  writeOffMinCents: cents(25_000),
  countAdjustPct: '10',
  countAdjustMinCents: cents(25_000),
});

/** Account setting keys of the thresholds (values: integer cents, a number for the %). */
export const APPROVAL_SETTING_KEYS = {
  writeOffMinCents: 'approval.writeoff_min_cents',
  countAdjustPct: 'approval.count_adjust_pct',
  countAdjustMinCents: 'approval.count_adjust_min_cents',
} as const satisfies Record<keyof ApprovalThresholds, string>;

/**
 * Account setting key of the time of day (local `HH:mm`) the daily state list is made for
 * each branch: the start of the trading day's stock work.
 */
export const STATE_LIST_TIME_SETTING_KEY = 'state.daily_list_time';
/** Default time the daily state list is made (local). */
export const DEFAULT_STATE_LIST_TIME = '04:00';

const settingCents = (value: unknown): Cents | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? cents(value) : null;

const settingPct = (value: unknown): string | null => {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : '';
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  const n = new D(text);
  return n.gt(0) && n.lte(100) ? n.toString() : null;
};

/**
 * Thresholds from account settings (`key → JSON value`), each falling back to its D-100
 * default when missing or malformed (a hand-edited setting never disables approvals).
 */
export function approvalThresholdsFromSettings(
  settings: ReadonlyMap<string, unknown>,
): ApprovalThresholds {
  const d = DEFAULT_APPROVAL_THRESHOLDS;
  return {
    writeOffMinCents:
      settingCents(settings.get(APPROVAL_SETTING_KEYS.writeOffMinCents)) ?? d.writeOffMinCents,
    countAdjustPct:
      settingPct(settings.get(APPROVAL_SETTING_KEYS.countAdjustPct)) ?? d.countAdjustPct,
    countAdjustMinCents:
      settingCents(settings.get(APPROVAL_SETTING_KEYS.countAdjustMinCents)) ??
      d.countAdjustMinCents,
  };
}

/** The daily state list time from account settings (local `HH:mm`), else the default. */
export function stateListTimeFromSettings(settings: ReadonlyMap<string, unknown>): string {
  const value = settings.get(STATE_LIST_TIME_SETTING_KEY);
  return typeof value === 'string' && /^\d{2}:\d{2}$/.test(value) && isCutoff(value)
    ? value
    : DEFAULT_STATE_LIST_TIME;
}

/** What {@link requiresApproval} judges. */
export interface ApprovalInput {
  /** The change (a write-off's quantity, a count's net adjustment); the sign is ignored. */
  qty: QtyInput;
  /** Standard cost per base unit, or `null` when the product has none. */
  unitCostCents: Cents | null;
  /** The level the change applies to, before it (a count: the system level at the count). */
  levelBefore: QtyInput;
}

/** Why approval is (or is not) needed. */
export type ApprovalGround =
  /** Worth at least the rand threshold. */
  | 'value'
  /** At least the percentage threshold of the level before. */
  | 'percent'
  /** No cost and no positive level: neither rule can be judged, so a person looks. */
  | 'cannot_judge';

/** Result of {@link requiresApproval}. */
export interface ApprovalCheck {
  required: boolean;
  /** Grounds that apply (empty when not required). */
  grounds: ApprovalGround[];
  /** |qty| × unit cost, half up to the cent; `null` without a cost. */
  valueCents: Cents | null;
  /** |qty| ÷ level before × 100 (exact decimal string); `null` when the level is ≤ 0. */
  percent: string | null;
  /**
   * `no_cost`: the product has no standard cost, so approval was judged by percentage only
   * ("no cost — approval by % only").
   */
  flags: Flag[];
}

/**
 * Whether a write-off or a count adjustment needs a manager's approval (D-100):
 *
 * - **write-off**: worth ≥ `writeOffMinCents` (R250.00 needs approval, R249.99 does not);
 * - **count adjustment**: worth ≥ `countAdjustMinCents`, **or** ≥ `countAdjustPct` of the
 *   level before (10% needs approval, 9.99% does not). The percentage only applies to a
 *   positive level before: finding stock where the system had none is judged by value.
 * - **No standard cost** (either kind): judged by percentage only (`countAdjustPct` of the
 *   level before) and flagged `no_cost`; with no cost **and** no positive level before,
 *   nothing can be judged, so approval is required (`cannot_judge`).
 *
 * A zero change never needs approval.
 */
export function requiresApproval(
  kind: ThresholdKind,
  input: ApprovalInput,
  thresholds: ApprovalThresholds,
): ApprovalCheck {
  const change = absQty(input.qty);
  const level = qty(input.levelBefore);
  const valueCents = input.unitCostCents === null ? null : mulQty(input.unitCostCents, change);
  const percent = isPositive(level) ? change.div(level).times(100).toString() : null;
  const flags: Flag[] = [];
  if (isZero(change)) return { required: false, grounds: [], valueCents, percent, flags };
  if (valueCents === null) {
    flags.push({
      code: 'no_cost',
      message: 'No cost — approval by % only.',
    });
  }
  const grounds: ApprovalGround[] = [];
  const minCents =
    kind === 'write_off' ? thresholds.writeOffMinCents : thresholds.countAdjustMinCents;
  if (valueCents !== null && valueCents >= minCents) grounds.push('value');
  // The percentage rule: always for counts; for write-offs only when there is no cost.
  const usePercent = kind === 'count_adjustment' || valueCents === null;
  if (usePercent && percent !== null && new D(percent).gte(thresholds.countAdjustPct)) {
    grounds.push('percent');
  }
  if (valueCents === null && percent === null) grounds.push('cannot_judge');
  return { required: grounds.length > 0, grounds, valueCents, percent, flags };
}

/** zod schema of a manager-PIN approval carried by a physical command (plan §10.2). */
export const managerPinApprovalSchema = z.object({
  mode: z.literal('manager_pin'),
  /** The manager whose PIN the station verified on the spot. */
  approved_by_staff_id: z.uuid(),
  /** When the manager entered the PIN (device clock). */
  approved_at: z.iso.datetime({ offset: true }),
});
/** A manager-PIN approval on a command. */
export type ManagerPinApproval = z.infer<typeof managerPinApprovalSchema>;
